import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";
import { LIGHTBULB_URL, TIP_WIDTH } from "@/constants/tutorial";
import type { StartHereTipProps, TooltipPos } from "@/interfaces/tutorial";
import { measureAnchor } from "@/utils/tutorial-helper";
import { UpArrowNotch } from "./tip-arrows";

export default function StartHereTip({ open, onClose, onGotIt }: StartHereTipProps) {
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<TooltipPos>(measureAnchor);
  // `entered` drives the CSS transition — false = offscreen right, true = in place
  const [entered, setEntered] = useState(false);
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Slide-in on open
  useEffect(() => {
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
    if (open) {
      setPos(measureAnchor());
      setMounted(true);
      // One rAF so the browser paints the off-screen start position before we
      // set entered=true and trigger the transition.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntered(true));
      });
    } else {
      // Slide out, then unmount
      setEntered(false);
      unmountTimerRef.current = setTimeout(() => setMounted(false), 820);
    }
    return () => {
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
    };
  }, [open]);

  // Re-anchor when the viewport resizes
  useEffect(() => {
    if (!mounted) return;
    function onResize() {
      setPos(measureAnchor());
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mounted]);

  if (!mounted) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: TIP_WIDTH,
        maxWidth: "calc(100vw - 32px)",
        background: "var(--color-white)",
        border: "1px solid var(--color-smoke)",
        borderRadius: 14,
        boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
        padding: "18px 20px",
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        lineHeight: 1.5,
        color: "var(--color-rodeo-black)",
        zIndex: 1000,
        // Slide in from the right when entering, slide back out when leaving
        transform: entered ? "translateX(0)" : "translateX(calc(100% + 32px))",
        opacity: entered ? 1 : 0,
        transition: entered
          ? "transform 1.0s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1)"
          : "transform 0.8s cubic-bezier(0.5, 0, 0.75, 0), opacity 0.5s ease-in",
      }}
    >
      {/* Upward-pointing arrow aligned with Customize button center */}
      <UpArrowNotch left={pos.arrowLeft} />

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-1.5 right-2 flex items-center justify-center bg-transparent border-none text-graphite hover:text-rodeo-black hover:bg-clear-dust rounded-sm cursor-pointer p-1 transition-colors"
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </button>

      {/* Lightbulb illustration */}
      <div
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 90,
          height: 90,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={LIGHTBULB_URL}
          alt=""
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 12,
          minHeight: 64,
          paddingRight: 20,
        }}
      >
        <p
          style={{
            margin: 0,
            color: "var(--color-rodeo-black)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          Start here if you want to customize the email your recipients will receive.
        </p>

        <button
          type="button"
          onClick={onGotIt}
          className="self-start inline-flex items-center bg-rodeo-black hover:bg-background-dark text-white border-none rounded-btn py-2 px-4 font-lato text-[11px] font-bold uppercase tracking-[1.5px] cursor-pointer transition-colors"
        >
          OK, got it
        </button>
      </div>
    </div>
  );
}
